// Crop & rotate maths (FR-23). The crop frame lives on the *displayed* image
// (already rotated and flipped) in 0–1 units; toSourceCrop turns it back into
// pixels of the upright original for ImageJob.crop.
import type { Rotation } from "@/lib/image/job";
import type { Rect, Size } from "@/lib/image/size";

/** x, y, width, height as fractions (0–1) of the displayed image. */
export type NormRect = { x: number; y: number; w: number; h: number };
export type Corner = "nw" | "ne" | "sw" | "se";

export const FULL: NormRect = { x: 0, y: 0, w: 1, h: 1 };
/** Smallest frame side, in displayed pixels. */
export const MIN_SIDE = 16;

export function rotateBy(rotate: Rotation, quarterTurns: number): Rotation {
  return ((((rotate + quarterTurns * 90) % 360) + 360) % 360) as Rotation;
}

/** The largest centred frame with this width:height ratio (null = whole image). */
export function fitRatio(display: Size, ratio: number | null): NormRect {
  if (!ratio) return FULL;
  let w = display.width;
  let h = w / ratio;
  if (h > display.height) {
    h = display.height;
    w = h * ratio;
  }
  const nw = w / display.width;
  const nh = h / display.height;
  return { x: (1 - nw) / 2, y: (1 - nh) / 2, w: nw, h: nh };
}

const clamp = (v: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, v));

/** Drag the whole frame by (dx, dy) fractions, staying inside the image. */
export function moveRect(r: NormRect, dx: number, dy: number): NormRect {
  return {
    ...r,
    x: clamp(r.x + dx, 0, 1 - r.w),
    y: clamp(r.y + dy, 0, 1 - r.h),
  };
}

/** Drag one corner by (dx, dy) fractions; the opposite corner stays put.
 *  With a ratio the frame keeps its shape. */
export function resizeRect(
  r: NormRect,
  corner: Corner,
  dx: number,
  dy: number,
  display: Size,
  ratio: number | null,
): NormRect {
  const W = display.width;
  const H = display.height;
  const east = corner === "ne" || corner === "se";
  const south = corner === "sw" || corner === "se";
  // Work in displayed pixels so the ratio is a real width:height ratio.
  const ax = (east ? r.x : r.x + r.w) * W;
  const ay = (south ? r.y : r.y + r.h) * H;
  const px = ((east ? r.x + r.w : r.x) + dx) * W;
  const py = ((south ? r.y + r.h : r.y) + dy) * H;
  const roomW = east ? W - ax : ax;
  const roomH = south ? H - ay : ay;
  const min = Math.min(MIN_SIDE, roomW, roomH);

  let w = clamp(east ? px - ax : ax - px, min, roomW);
  let h = clamp(south ? py - ay : ay - py, min, roomH);
  if (ratio) {
    if (w / h > ratio) w = h * ratio;
    else h = w / ratio;
    // Keep inside the image without losing the shape.
    const fit = Math.min(1, roomW / w, roomH / h);
    w *= fit;
    h *= fit;
  }
  return {
    x: (east ? ax : ax - w) / W,
    y: (south ? ay : ay - h) / H,
    w: w / W,
    h: h / H,
  };
}

/** The frame in pixels of the upright original, undoing the flip and then
 *  the clockwise rotation that the display (and the output) apply. */
export function toSourceCrop(
  r: NormRect,
  source: Size,
  rotate: Rotation,
  flipH: boolean,
  flipV: boolean,
): Rect {
  const x = flipH ? 1 - r.x - r.w : r.x;
  const y = flipV ? 1 - r.y - r.h : r.y;
  let s: NormRect;
  switch (rotate) {
    case 90:
      s = { x: y, y: 1 - x - r.w, w: r.h, h: r.w };
      break;
    case 180:
      s = { x: 1 - x - r.w, y: 1 - y - r.h, w: r.w, h: r.h };
      break;
    case 270:
      s = { x: 1 - y - r.h, y: x, w: r.h, h: r.w };
      break;
    default:
      s = { x, y, w: r.w, h: r.h };
  }
  const left = Math.round(s.x * source.width);
  const top = Math.round(s.y * source.height);
  return {
    x: left,
    y: top,
    width: Math.max(
      1,
      Math.min(source.width - left, Math.round(s.w * source.width)),
    ),
    height: Math.max(
      1,
      Math.min(source.height - top, Math.round(s.h * source.height)),
    ),
  };
}
